import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { clearRequestCache } from '@/lib/requestCache';
import { authService, type LoginResponse, type User } from '@/services/authService';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  emailVerificationRequired: boolean;
  /** F067: true when the session was terminated by an auth:unauthorized event
   * while a user was logged in (i.e. token expired mid-session). Cleared on
   * the next successful login(). ProtectedRoute reads this to pass
   * flash='session_expired' in the Navigate state. */
  sessionExpired: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): void;
  refreshUser(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Ref so the auth:unauthorized handler can read the latest user value
  // without being re-created on every render (avoids stale-closure issues).
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  useEffect(() => {
    const token = localStorage.getItem('jwt');
    if (!token) {
      setIsLoading(false);
      return;
    }
    authService
      .getMe()
      .then((u) => {
        setUser(u);
        setEmailVerificationRequired(u.email_verified_at === null);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const handler = () => {
      // Only mark the session as expired when the user WAS authenticated —
      // unauthenticated 401s (e.g. from a public endpoint) must not set it.
      if (userRef.current !== null) {
        setSessionExpired(true);
      }
      setUser(null);
      setEmailVerificationRequired(false);
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response: LoginResponse = await authService.login({ email, password });
    localStorage.setItem('jwt', response.access_token);
    setUser(response.user);
    setEmailVerificationRequired(response.email_verification_required);
    // Clear the session-expired flag on successful login.
    setSessionExpired(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('jwt');
    // F070 — drop cached shell GETs so a next login on this browser never
    // sees the previous account's data (auth:unauthorized also clears it).
    clearRequestCache();
    setUser(null);
    setEmailVerificationRequired(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await authService.getMe();
    setUser(u);
    setEmailVerificationRequired(u.email_verified_at === null);
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isAdmin: user?.role === 'admin',
    isLoading,
    emailVerificationRequired,
    sessionExpired,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
